import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import Dropdown from '../../../_custom/Dropdown';
import { DropdownOption } from '../../../_custom/Dropdown/dropdownTypes';
import { Input } from '../../../_custom';
import { fetchGstOverviewData } from '../../../../store/dashboard/gstOverview/apiHelper';
import { GstOverviewRow } from '../../../../store/dashboard/gstOverview/types';
import {
    BUSINESS_ADDRESS_OWNERSHIP_OPTIONS,
    BUSINESS_DETAILS_FIELD_KEYS,
    BUSINESS_DETAILS_KEY,
    BUSINESS_DETAILS_LABELS,
    BUSINESS_LOAN_PURPOSE_OPTIONS,
    GST_STATUS_ACTIVE,
} from '../../DealDrawerConstants';
import styles from '../../ManageDealDrawer.module.scss';

interface BusinessDetailsSectionProps {
    formData: Record<string, any>;
    handleChange: (key: string, value: any, importanceKey?: string | null) => void;
    assesseeOrgId?: string;
    readonly?: boolean;
}

const businessDetailsFieldKey = (key: string) => `${BUSINESS_DETAILS_KEY}.${key}`;

// Business Details is optional mandate-level data - it never gates the drawer's Proceed button,
// and every value collects into a single `businessDetails` object (see useManageDealDrawer's
// handleSaveMandateApplicants) rather than companyMetrics/dealTerms.
export const BusinessDetailsSection: React.FC<BusinessDetailsSectionProps> = ({ formData, handleChange, assesseeOrgId, readonly }) => {
    const [activeGstRows, setActiveGstRows] = useState<GstOverviewRow[]>([]);
    const [selectedGstin, setSelectedGstin] = useState('');
    const [enteredAddressManually, setEnteredAddressManually] = useState(false);
    const [showOthersInput, setShowOthersInput] = useState(false);

    const gstinKey = businessDetailsFieldKey(BUSINESS_DETAILS_FIELD_KEYS.GSTIN);
    const businessAddressKey = businessDetailsFieldKey(BUSINESS_DETAILS_FIELD_KEYS.BUSINESS_ADDRESS);
    const businessAddressOwnershipKey = businessDetailsFieldKey(BUSINESS_DETAILS_FIELD_KEYS.BUSINESS_ADDRESS_OWNERSHIP);
    const loanPurposeKey = businessDetailsFieldKey(BUSINESS_DETAILS_FIELD_KEYS.LOAN_PURPOSE);

    const savedGstinValue = formData[gstinKey] || '';

    const businessAddressValue = formData[businessAddressKey] || '';
    const businessAddressOwnershipValue = formData[businessAddressOwnershipKey] || '';
    const loanPurposeValue = formData[loanPurposeKey] || '';

    useEffect(() => {
        setActiveGstRows([]);
        setSelectedGstin('');
        setEnteredAddressManually(false);
        if (!assesseeOrgId) return;
        let ignore = false;
        fetchGstOverviewData(assesseeOrgId).then((response) => {
            if (ignore) return;
            setActiveGstRows((response?.rows || []).filter((row) => row.status === GST_STATUS_ACTIVE));
        });
        return () => {
            ignore = true;
        };
    }, [assesseeOrgId]);

    const selectedLoanPurposeOption =
        BUSINESS_LOAN_PURPOSE_OPTIONS.find((opt) => opt.id === loanPurposeValue && opt.id !== BUSINESS_DETAILS_LABELS.othersOptionId) ?? null;
    const isKnownLoanPurposeOption = !!selectedLoanPurposeOption;

    // Detects a previously saved free-text purpose (not one of the dropdown's fixed options) so
    // reopening the drawer restores the "Others" free-text input instead of an empty dropdown.
    useEffect(() => {
        if (loanPurposeValue && !isKnownLoanPurposeOption) setShowOthersInput(true);
    }, [loanPurposeValue, isKnownLoanPurposeOption]);

    // Restores the GST dropdown selection from a previously saved gstin so reopening the drawer
    // shows the chosen GSTIN instead of an empty picker.
    useEffect(() => {
        if (savedGstinValue && !enteredAddressManually) setSelectedGstin(savedGstinValue);
    }, [savedGstinValue, enteredAddressManually]);

    const handleGstSelect = useCallback(
        (option: DropdownOption) => {
            // The Dropdown's own clear (x) calls back with an empty id - reset GST selection and
            // Business Address together.
            if (!option.id) {
                setEnteredAddressManually(false);
                setSelectedGstin('');
                handleChange(gstinKey, '');
                handleChange(businessAddressKey, '');
                return;
            }
            if (option.id === BUSINESS_DETAILS_LABELS.enterAddressManuallyOptionId) {
                setEnteredAddressManually(true);
                setSelectedGstin('');
                handleChange(gstinKey, '');
                handleChange(businessAddressKey, '');
                return;
            }
            setEnteredAddressManually(false);
            const gstin = String(option.id);
            setSelectedGstin(gstin);
            handleChange(gstinKey, gstin);
            const matchedRow = activeGstRows.find((row) => row.gstin === gstin);
            // Always resync, not just when the new row has an address - switching to an
            // address-less GSTIN must clear a stale address left over from the prior selection.
            handleChange(businessAddressKey, matchedRow?.address || '');
        },
        [activeGstRows, businessAddressKey, gstinKey, handleChange],
    );

    const handleLoanPurposeSelect = useCallback(
        (option: DropdownOption) => {
            if (option.id === BUSINESS_DETAILS_LABELS.othersOptionId) {
                setShowOthersInput(true);
                handleChange(loanPurposeKey, '');
            } else {
                setShowOthersInput(false);
                handleChange(loanPurposeKey, option.id);
            }
        },
        [handleChange, loanPurposeKey],
    );

    return (
        <>
            <div className={styles.Section}>
                <div className={styles.SectionHeading}>{BUSINESS_DETAILS_LABELS.heading}</div>

                <div className={styles.InputList}>
                        <div className={styles.InputItem}>
                            <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{BUSINESS_DETAILS_LABELS.gstLabel}</div>
                            <div className={styles.FieldFullWidthWrapper}>
                                <Dropdown
                                    selectedOption={
                                        enteredAddressManually
                                            ? {
                                                  id: BUSINESS_DETAILS_LABELS.enterAddressManuallyOptionId,
                                                  label: BUSINESS_DETAILS_LABELS.enterAddressManuallyLabel,
                                              }
                                            : selectedGstin
                                            ? { id: selectedGstin, label: selectedGstin }
                                            : null
                                    }
                                    optionsList={[
                                        ...activeGstRows.map((row) => ({ id: row.gstin, label: row.gstin, subLabel: row.address })),
                                        {
                                            id: BUSINESS_DETAILS_LABELS.enterAddressManuallyOptionId,
                                            label: BUSINESS_DETAILS_LABELS.enterAddressManuallyLabel,
                                        },
                                    ]}
                                    placeholder={BUSINESS_DETAILS_LABELS.gstPlaceholder}
                                    itemClickHandler={handleGstSelect}
                                    style={{ width: '100%', height: '44px' }}
                                    disabled={readonly}
                                    showFloatingLabel={false}
                                />
                            </div>
                        </div>

                        <div className={styles.InputItem}>
                            <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{BUSINESS_DETAILS_LABELS.businessAddressLabel}</div>
                            <div className={styles.FieldFullWidthWrapper}>
                                <Input
                                    type="text"
                                    value={businessAddressValue}
                                    placeholder={BUSINESS_DETAILS_LABELS.businessAddressPlaceholder}
                                    onChange={(value: string) => handleChange(businessAddressKey, value)}
                                    containerClassName={styles.FieldControlSm}
                                    disabled={readonly}
                                    allowAllCharacters
                                    showFloatingLabel={false}
                                />
                            </div>
                        </div>

                        <div className={styles.InputItem}>
                            <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>
                                {BUSINESS_DETAILS_LABELS.businessAddressOwnershipLabel}
                            </div>
                            <div className={styles.FieldFullWidthWrapper}>
                                <Dropdown
                                    selectedOption={
                                        businessAddressOwnershipValue
                                            ? { id: businessAddressOwnershipValue, label: businessAddressOwnershipValue }
                                            : null
                                    }
                                    optionsList={BUSINESS_ADDRESS_OWNERSHIP_OPTIONS}
                                    placeholder={BUSINESS_DETAILS_LABELS.businessAddressOwnershipPlaceholder}
                                    itemClickHandler={(option) => handleChange(businessAddressOwnershipKey, option.id || '')}
                                    style={{ width: '100%', height: '44px' }}
                                    disabled={readonly}
                                    showFloatingLabel={false}
                                />
                            </div>
                        </div>

                        <div className={styles.InputItem}>
                            <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{BUSINESS_DETAILS_LABELS.loanPurposeLabel}</div>
                            <div className={styles.FieldFullWidthWrapper}>
                                <Dropdown
                                    enableSearch
                                    selectedOption={
                                        showOthersInput
                                            ? {
                                                  id: BUSINESS_DETAILS_LABELS.othersOptionId,
                                                  label: BUSINESS_DETAILS_LABELS.othersOptionId,
                                              }
                                            : selectedLoanPurposeOption
                                    }
                                    optionsList={BUSINESS_LOAN_PURPOSE_OPTIONS}
                                    placeholder={BUSINESS_DETAILS_LABELS.loanPurposePlaceholder}
                                    itemClickHandler={handleLoanPurposeSelect}
                                    style={{ width: '100%', height: '44px' }}
                                    disabled={readonly}
                                    showFloatingLabel={false}
                                />
                            </div>
                        </div>

                        {showOthersInput && (
                            <div className={styles.InputItem}>
                                <div className={clsx(styles.InputLabel, styles.InputItemLabel)}>{BUSINESS_DETAILS_LABELS.othersInputLabel}</div>
                                <div className={styles.FieldFullWidthWrapper}>
                                    <Input
                                        type="text"
                                        value={loanPurposeValue}
                                        placeholder={BUSINESS_DETAILS_LABELS.othersInputPlaceholder}
                                        onChange={(value: string) => handleChange(loanPurposeKey, value)}
                                        containerClassName={styles.FieldControlSm}
                                        disabled={readonly}
                                        allowAllCharacters
                                        showFloatingLabel={false}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
            </div>

            <div className={styles.PecDivider} />
        </>
    );
};

export default BusinessDetailsSection;
